import { inject, Injectable } from '@angular/core';
import { DeviceIdSource } from '@shared/models/cash-register';
import { DeviceInfo } from '@shared/models/device-info';
import { EdaraNativeService } from './edara-native.service';
import { KnownDeviceIdentityService } from './known-device-identity.service';

/** What a cash register pairs with: the Known Devices id of this browser (Web) or the Desktop app's physical device id. */
export interface DeviceIdentity {
  id: string;
  source: DeviceIdSource;
  /** Web only: the browser family, e.g. `Chrome`. */
  browser?: string;
  /** Web only: the operating system family, e.g. `Windows`. */
  os?: string;
}

const WEB_ID_STORAGE_KEY = 'edaraPosWebDeviceId';

/**
 * The identity of the device the POS runs on — the single resolver for the
 * id cash registers are paired with. The Web id is the Known Devices
 * `deviceId` resolved at login (kept in localStorage so it still answers
 * offline); the Desktop id comes from the Desktop app (`GET /api/device/info`),
 * cached after the first answer. The cache only spares the slow hardware
 * query: whether the Desktop app is running is probed on every resolve, so a
 * closed app is noticed without a logout.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdentityService {
  private readonly edaraNativeService = inject(EdaraNativeService);
  private readonly knownDeviceIdentityService = inject(KnownDeviceIdentityService);
  private deviceInfo: DeviceInfo | undefined;
  private pending: Promise<DeviceInfo | undefined> | undefined;

  /** The Desktop app's device info, or `undefined` while it cannot be resolved (a failed lookup is retried). */
  getDeviceInfoAsync(): Promise<DeviceInfo | undefined> {
    if (this.deviceInfo) return Promise.resolve(this.deviceInfo);
    return this.fetchDeviceInfoAsync();
  }

  /** The identity a register with this source pairs with; `undefined` when it cannot be resolved (a closed Desktop app included). */
  async resolveAsync(source: DeviceIdSource): Promise<DeviceIdentity | undefined> {
    if (source === DeviceIdSource.Web) return this.resolveWebIdentityAsync();
    if (!(await this.isDesktopRunningAsync())) return undefined;
    const info = await this.getDeviceInfoAsync();
    return info?.DeviceId ? { id: info.DeviceId, source: DeviceIdSource.Desktop } : undefined;
  }

  /**
   * Liveness only, never answered from the cache: the version endpoint answers
   * at once, and older Desktop builds without it are asked for their device info.
   */
  async isDesktopRunningAsync(): Promise<boolean> {
    if (await this.edaraNativeService.getVersionAsync()) return true;
    return !!(await this.fetchDeviceInfoAsync())?.DeviceId;
  }

  async getDesktopVersionAsync(): Promise<string | undefined> {
    return (await this.edaraNativeService.getVersionAsync())?.version || undefined;
  }

  /** Asks the Desktop app, sharing one in-flight lookup; its answer (or lack of one) replaces the cache. */
  private fetchDeviceInfoAsync(): Promise<DeviceInfo | undefined> {
    if (!this.pending) {
      this.pending = this.edaraNativeService.getDeviceInfoAsync()
        .then(info => {
          this.deviceInfo = info ?? undefined;
          return this.deviceInfo;
        })
        .catch(() => undefined)
        .finally(() => (this.pending = undefined));
    }
    return this.pending;
  }

  /** Login resolves the Known Devices id online; the last answer is kept so an offline session keeps the same id. */
  private async resolveWebIdentityAsync(): Promise<DeviceIdentity | undefined> {
    const { deviceId } = await this.knownDeviceIdentityService.resolveDevice();
    if (deviceId) writeStorage(WEB_ID_STORAGE_KEY, deviceId);
    const id = deviceId ?? readStorage(WEB_ID_STORAGE_KEY);
    return id ? { id, source: DeviceIdSource.Web, browser: browserFamily(), os: osFamily() } : undefined;
  }
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable: the identity still works for this session
  }
}

type NavigatorWithUaData = Navigator & { userAgentData?: { platform?: string; brands?: { brand: string }[] } };

function browserFamily(): string | undefined {
  const brands = (navigator as NavigatorWithUaData).userAgentData?.brands ?? [];
  const brand = brands.map(entry => entry.brand).find(name => !/not.{0,2}a.{0,2}brand|chromium/i.test(name));
  if (brand) return brand.replace(/^(Google|Microsoft) /, '');

  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return undefined;
}

function osFamily(): string | undefined {
  const source = (navigator as NavigatorWithUaData).userAgentData?.platform || navigator.userAgent;
  if (/iphone|ipad|ipod|ios/i.test(source)) return 'iOS';
  if (/android/i.test(source)) return 'Android';
  if (/win/i.test(source)) return 'Windows';
  if (/mac/i.test(source)) return 'macOS';
  if (/linux|x11|cros/i.test(source)) return 'Linux';
  return undefined;
}
